import { useEffect, useMemo, useState } from 'react';
import {
  KeyRound, Plus, ShieldCheck, FileSignature, X, Loader2, RefreshCw,
  Power, PowerOff, CalendarClock, Copy, Check,
} from 'lucide-react';
import { PageHeader, Section } from '../components/Scaffold';
import useAuthStore from '../stores/authStore';
import api from '../api/client';

// role in ("platform_owner", "platform_user") — see User.access_level's
// comment in backend/app/models.py; same pattern as Announcements.jsx /
// SupportTickets.jsx.
const PLATFORM_ACCESS_ROLES = ['platform_owner', 'platform_user'];

/**
 * License management — issue, sign and track per-café license keys.
 *
 * Backend: app/api/endpoints/license.py, mounted at /api/license.
 *   GET  /license/                list licenses (superadmin/platform see all)
 *   POST /license/                issue a license with an explicit key
 *   POST /license/auto            issue a license with an auto-generated signed key
 *   POST /license/signed/generate sign a key WITHOUT persisting (offline provisioning)
 *   POST /license/revoke/{key}    suspend (also suspends every PC bound to it)
 *   POST /license/activate/{key}  reactivate
 *   POST /license/extend/{key}    push out expires_at
 *
 * Write actions require superadmin, or a platform_owner/platform_user account
 * with access_level "write" (require_portal_access(write=True) on the backend).
 *
 * Not wired here: GET /license/platform (pooled game-platform accounts) —
 * unrelated to café licensing, and currently 500s (queries a
 * `platform_accounts` table that only exists per-café, in models_cafe.py,
 * not in the global DB this router reads from — dead code from before the
 * multi-DB split).
 */

const STATUS_COLOR = { active: 'success', expiring: 'warning', expired: 'danger', suspended: 'neutral' };
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// Manual keys are short ("PRMS-…"), but an auto-generated signed key is a
// full JWT (150-250+ chars) — rendered raw in a table cell it blows out the
// column and pushes Café/Max PCs/Expires/Status off the (overflow: hidden)
// card edge, not just off-screen but genuinely unreachable. Shorten for the
// table; the full key is still shown in the Manage modal and copyable from
// the Generate-signed-key result box.
function shortKey(key) {
  return key.length <= 28 ? key : `${key.slice(0, 14)}…${key.slice(-10)}`;
}

function licenseStatus(lic) {
  if (lic.is_active === false) return 'suspended';
  if (!lic.expires_at) return 'active';
  const expires = new Date(lic.expires_at).getTime();
  const now = Date.now();
  if (expires < now) return 'expired';
  if (expires < now + SEVEN_DAYS_MS) return 'expiring';
  return 'active';
}

// <input type="date"> gives "YYYY-MM-DD"; the backend wants a real datetime.
function toIsoDate(dateStr) {
  return dateStr ? new Date(`${dateStr}T00:00:00Z`).toISOString() : null;
}

function oneYearFromNow() {
  const d = new Date();
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

export default function Licenses() {
  const { isSuperAdmin, user } = useAuthStore();
  const isPlatformRole = PLATFORM_ACCESS_ROLES.includes(user?.role);
  const canWrite = isSuperAdmin() || (isPlatformRole && user?.access_level === 'write');

  const [licenses, setLicenses] = useState([]);
  const [cafes, setCafes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const [showIssue, setShowIssue] = useState(false);
  const [issueForm, setIssueForm] = useState({ cafeId: '', maxPcs: '', expiresAt: oneYearFromNow(), key: '' });
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState(null);

  const [signModal, setSignModal] = useState(false);
  const [signForm, setSignForm] = useState({ cafeId: '', maxPcs: '', expiresAt: '' });
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState(null);
  const [signResult, setSignResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const [managing, setManaging] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [extendDays, setExtendDays] = useState('30');

  const fetchAll = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [licRes, cafeRes] = await Promise.all([
        api.get('/license/'),
        api.get('/internal/cafes'),
      ]);
      setLicenses(licRes.data || []);
      setCafes(cafeRes.data || []);
    } catch (err) {
      console.error('Failed to fetch licenses:', err);
      setError(err.response?.data?.detail || 'Failed to load licenses');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const cafeName = useMemo(() => {
    const map = new Map(cafes.map((c) => [c.id, c.name]));
    return (id) => map.get(id) || `Café ${id}`;
  }, [cafes]);

  const stats = useMemo(() => {
    const withStatus = licenses.map(licenseStatus);
    return {
      active: withStatus.filter((s) => s === 'active').length,
      expiring: withStatus.filter((s) => s === 'expiring').length,
      expired: withStatus.filter((s) => s === 'expired').length,
    };
  }, [licenses]);

  const filteredLicenses = useMemo(() => {
    if (statusFilter === 'all') return licenses;
    return licenses.filter((l) => licenseStatus(l) === statusFilter);
  }, [licenses, statusFilter]);

  const resetIssueForm = () => setIssueForm({ cafeId: '', maxPcs: '', expiresAt: oneYearFromNow(), key: '' });

  const submitIssue = async (e) => {
    e.preventDefault();
    if (!issueForm.cafeId || !issueForm.maxPcs || !issueForm.expiresAt) {
      setIssueError('Café, max PCs and expiry are required');
      return;
    }
    setIssuing(true);
    setIssueError(null);
    try {
      const expiresIso = toIsoDate(issueForm.expiresAt);
      const cafeId = Number(issueForm.cafeId);
      const maxPcs = Number(issueForm.maxPcs);
      if (issueForm.key.trim()) {
        await api.post('/license/', { key: issueForm.key.trim(), cafe_id: cafeId, expires_at: expiresIso, max_pcs: maxPcs });
      } else {
        await api.post('/license/auto', null, { params: { cafe_id: cafeId, expires_at: expiresIso, max_pcs: maxPcs } });
      }
      setShowIssue(false);
      resetIssueForm();
      await fetchAll();
    } catch (err) {
      setIssueError(err.response?.data?.detail || 'Failed to issue license');
    } finally {
      setIssuing(false);
    }
  };

  const submitSign = async (e) => {
    e.preventDefault();
    if (!signForm.cafeId || !signForm.maxPcs) {
      setSignError('Café and max PCs are required');
      return;
    }
    setSigning(true);
    setSignError(null);
    setSignResult(null);
    try {
      const params = { cafe_id: Number(signForm.cafeId), max_pcs: Number(signForm.maxPcs) };
      if (signForm.expiresAt) params.expires_at = toIsoDate(signForm.expiresAt);
      const res = await api.post('/license/signed/generate', null, { params });
      setSignResult(res.data);
    } catch (err) {
      setSignError(err.response?.data?.detail || 'Failed to generate signed key');
    } finally {
      setSigning(false);
    }
  };

  const copyKey = async () => {
    if (!signResult?.key) return;
    try {
      await navigator.clipboard.writeText(signResult.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — the key is still selectable in the read-only box.
    }
  };

  const openManage = (lic) => {
    setManaging(lic);
    setActionError(null);
    setExtendDays('30');
  };

  const closeManage = () => {
    setManaging(null);
    setActionBusy(false);
    setActionError(null);
  };

  const toggleActive = async () => {
    if (!managing) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const path = managing.is_active === false ? 'activate' : 'revoke';
      await api.post(`/license/${path}/${encodeURIComponent(managing.key)}`);
      setManaging((m) => (m ? { ...m, is_active: m.is_active === false } : m));
      await fetchAll();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const submitExtend = async () => {
    const days = Number(extendDays);
    if (!managing || !days || days <= 0) return;
    setActionBusy(true);
    setActionError(null);
    try {
      // Stack onto the current expiry if it's still in the future; an
      // already-expired/suspended license extends from now instead, so
      // "+30 days" always means 30 days from today, not from a stale date.
      const currentExpiry = managing.expires_at ? new Date(managing.expires_at) : null;
      const base = currentExpiry && currentExpiry > new Date() ? currentExpiry : new Date();
      const newExpiry = new Date(base.getTime() + days * 24 * 60 * 60 * 1000).toISOString();
      await api.post(`/license/extend/${encodeURIComponent(managing.key)}`, null, { params: { new_expiry: newExpiry } });
      setManaging((m) => (m ? { ...m, expires_at: newExpiry } : m));
      await fetchAll();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to extend license');
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="page licenses-page">
      <PageHeader
        title="Licenses"
        subtitle="Issue, sign and track per-café license keys"
        actions={
          <>
            <button
              className="btn btn--secondary"
              onClick={() => { setSignModal(true); setSignResult(null); setSignError(null); }}
              disabled={!canWrite}
              title={!canWrite ? 'Read-only access' : undefined}
            >
              <FileSignature size={16} /> Generate signed key
            </button>
            <button
              className="btn btn--primary"
              onClick={() => setShowIssue((s) => !s)}
              disabled={!canWrite}
              title={!canWrite ? 'Read-only access' : undefined}
            >
              <Plus size={16} /> Issue license
            </button>
          </>
        }
      />

      <div className="quick-stats">
        <button
          className={`quick-stat ${statusFilter === 'all' ? 'quick-stat--active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          <span className="quick-stat__value">{isLoading ? '…' : licenses.length}</span>
          <span className="quick-stat__label">Total</span>
        </button>
        <button
          className={`quick-stat quick-stat--success ${statusFilter === 'active' ? 'quick-stat--active' : ''}`}
          onClick={() => setStatusFilter('active')}
        >
          <span className="quick-stat__value">{isLoading ? '…' : stats.active}</span>
          <span className="quick-stat__label">Active</span>
        </button>
        <button
          className={`quick-stat quick-stat--warning ${statusFilter === 'expiring' ? 'quick-stat--active' : ''}`}
          onClick={() => setStatusFilter('expiring')}
        >
          <span className="quick-stat__value">{isLoading ? '…' : stats.expiring}</span>
          <span className="quick-stat__label">Expiring ≤ 7d</span>
        </button>
        <button
          className={`quick-stat quick-stat--danger ${statusFilter === 'expired' ? 'quick-stat--active' : ''}`}
          onClick={() => setStatusFilter('expired')}
        >
          <span className="quick-stat__value">{isLoading ? '…' : stats.expired}</span>
          <span className="quick-stat__label">Expired</span>
        </button>
      </div>

      {showIssue && (
        <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
          <Section title="Issue a license" desc="POST /api/license/ (or /auto for a signed key)">
            <form onSubmit={submitIssue}>
              {issueError && <p className="modal-error">{issueError}</p>}
              <div className="form-grid">
                <div className="field">
                  <label className="field__label">Café</label>
                  <select
                    className="field__select"
                    value={issueForm.cafeId}
                    onChange={(e) => setIssueForm((f) => ({ ...f, cafeId: e.target.value }))}
                  >
                    <option value="">Select café…</option>
                    {cafes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label className="field__label">Max PCs</label>
                  <input
                    className="field__input" type="number" min="1" placeholder="25"
                    value={issueForm.maxPcs}
                    onChange={(e) => setIssueForm((f) => ({ ...f, maxPcs: e.target.value }))}
                  />
                </div>
                <div className="field">
                  <label className="field__label">Expires at</label>
                  <input
                    className="field__input" type="date"
                    value={issueForm.expiresAt}
                    onChange={(e) => setIssueForm((f) => ({ ...f, expiresAt: e.target.value }))}
                  />
                </div>
                <div className="field">
                  <label className="field__label">Key (blank = auto-generate)</label>
                  <input
                    className="field__input" placeholder="PRMS-…"
                    value={issueForm.key}
                    onChange={(e) => setIssueForm((f) => ({ ...f, key: e.target.value }))}
                  />
                </div>
              </div>
              <div className="page-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn--secondary" onClick={() => { setShowIssue(false); resetIssueForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary" disabled={issuing}>
                  {issuing ? <Loader2 size={16} className="spin" /> : <ShieldCheck size={16} />} {issuing ? 'Issuing…' : 'Issue'}
                </button>
              </div>
            </form>
          </Section>
        </div>
      )}

      <div className="card table-card">
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>License key</th>
              <th>Café</th>
              <th>Max PCs</th>
              <th>Expires</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty-state">
                    <Loader2 size={32} className="spin" />
                    <p>Loading licenses...</p>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty-state empty-state--error">
                    <p>{error}</p>
                    <button className="btn btn--secondary btn--sm" onClick={fetchAll}>
                      <RefreshCw size={14} /> Retry
                    </button>
                  </div>
                </td>
              </tr>
            ) : licenses.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty-state">
                    <KeyRound size={48} />
                    <p>No licenses issued yet</p>
                  </div>
                </td>
              </tr>
            ) : filteredLicenses.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty-state">
                    <KeyRound size={48} />
                    <p>No {statusFilter} licenses</p>
                    <button className="btn btn--secondary btn--sm" onClick={() => setStatusFilter('all')}>
                      Clear filter
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredLicenses.map((l) => {
                const status = licenseStatus(l);
                return (
                  <tr key={l.key}>
                    <td className="font-mono" title={l.key}><KeyRound size={14} /> {shortKey(l.key)}</td>
                    <td>{cafeName(l.cafe_id)}</td>
                    <td>{l.max_pcs}</td>
                    <td className="text-tertiary font-mono">
                      {l.expires_at ? new Date(l.expires_at).toLocaleDateString() : '—'}
                    </td>
                    <td><span className={`badge badge--${STATUS_COLOR[status]}`}>{status}</span></td>
                    <td><button className="btn btn--ghost btn--sm" onClick={() => openManage(l)}>Manage</button></td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {signModal && (
        <div className="modal-backdrop" onClick={() => setSignModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">Generate signed key</h2>
              <button className="modal__close" onClick={() => setSignModal(false)}><X size={20} /></button>
            </div>
            <div className="modal__body">
              <p className="modal-info">
                Signs a key without saving it — for handing to a café admin for offline provisioning.
                Nothing is recorded here; issue a real license above once it&apos;s in use.
              </p>
              {signError && <p className="modal-error">{signError}</p>}
              <form onSubmit={submitSign}>
                <div className="form-grid">
                  <div className="field">
                    <label className="field__label">Café</label>
                    <select
                      className="field__select"
                      value={signForm.cafeId}
                      onChange={(e) => setSignForm((f) => ({ ...f, cafeId: e.target.value }))}
                    >
                      <option value="">Select café…</option>
                      {cafes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label className="field__label">Max PCs</label>
                    <input
                      className="field__input" type="number" min="1"
                      value={signForm.maxPcs}
                      onChange={(e) => setSignForm((f) => ({ ...f, maxPcs: e.target.value }))}
                    />
                  </div>
                  <div className="field">
                    <label className="field__label">Expires at (optional)</label>
                    <input
                      className="field__input" type="date"
                      value={signForm.expiresAt}
                      onChange={(e) => setSignForm((f) => ({ ...f, expiresAt: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="page-actions" style={{ marginTop: 16 }}>
                  <button type="submit" className="btn btn--primary" disabled={signing}>
                    {signing ? <Loader2 size={16} className="spin" /> : <FileSignature size={16} />} {signing ? 'Signing…' : 'Generate'}
                  </button>
                </div>
              </form>
              {signResult && (
                <div className="field" style={{ marginTop: 16 }}>
                  <label className="field__label">Signed key</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="field__input font-mono" readOnly
                      value={signResult.key}
                      onFocus={(e) => e.target.select()}
                    />
                    <button type="button" className="btn btn--secondary btn--sm" onClick={copyKey}>
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {managing && (
        <div className="modal-backdrop" onClick={closeManage}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">Manage license</h2>
              <button className="modal__close" onClick={closeManage}><X size={20} /></button>
            </div>
            <div className="modal__body">
              <p className="modal-info font-mono">{managing.key}</p>
              <p className="text-secondary" style={{ marginTop: -8, marginBottom: 16 }}>
                {cafeName(managing.cafe_id)} · {managing.max_pcs} PCs ·{' '}
                <span className={`badge badge--${STATUS_COLOR[licenseStatus(managing)]}`}>{licenseStatus(managing)}</span>
              </p>
              {actionError && <p className="modal-error">{actionError}</p>}
              <div className="page-actions" style={{ marginBottom: 20 }}>
                <button
                  className={`btn ${managing.is_active === false ? 'btn--primary' : 'btn--secondary'}`}
                  onClick={toggleActive}
                  disabled={!canWrite || actionBusy}
                  title={!canWrite ? 'Read-only access' : undefined}
                >
                  {managing.is_active === false ? <Power size={16} /> : <PowerOff size={16} />}
                  {actionBusy ? 'Working…' : managing.is_active === false ? 'Activate' : 'Revoke'}
                </button>
              </div>
              {managing.is_active !== false && (
                <p className="text-tertiary" style={{ fontSize: 12, marginTop: -14, marginBottom: 20 }}>
                  Revoking also suspends every PC currently bound to this license.
                </p>
              )}
              <div className="field">
                <label className="field__label">Extend by (days)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="field__input" type="number" min="1" placeholder="30"
                    value={extendDays}
                    onChange={(e) => setExtendDays(e.target.value)}
                  />
                  <button
                    className="btn btn--secondary btn--sm"
                    onClick={submitExtend}
                    disabled={!canWrite || actionBusy || !extendDays || Number(extendDays) <= 0}
                    title={!canWrite ? 'Read-only access' : undefined}
                  >
                    <CalendarClock size={14} /> Extend
                  </button>
                </div>
                {extendDays && Number(extendDays) > 0 && (
                  <p className="text-tertiary" style={{ fontSize: 12, marginTop: 6 }}>
                    New expiry: {new Date(
                      (managing.expires_at && new Date(managing.expires_at) > new Date() ? new Date(managing.expires_at) : new Date()).getTime()
                      + Number(extendDays) * 24 * 60 * 60 * 1000
                    ).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .licenses-page {
          /* noLag rebrand: scoped to this page only. No Figma node exists
             for Licenses (it's not one of the designed super-admin
             screens — "Cafés & Licensing" is a nav group in the real app,
             not a distinct Figma frame), so applied by the same pattern as
             CafeEdit/CafeOnboard rather than verified against a design.
             IMPORTANT: STATUS_COLOR (active/expiring/expired/suspended) is
             a separate --status-* token, untouched by this — including the
             .quick-stat--success.quick-stat--active compound-class trick
             below, which deliberately shows the real status color instead
             of this accent when a status filter is the active one. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
        }
        .quick-stats { display: flex; gap: var(--space-3); margin-bottom: var(--space-6); flex-wrap: wrap; }
        .quick-stat {
          /* Same white-sheen recipe as CafeRegistry's identical quick-stat
             pattern (Figma-confirmed there on 2468:164 etc.) — not nested
             inside another glass card, so its own backdrop-filter is safe. */
          flex: 1; min-width: 140px;
          padding: 10px var(--space-6);
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          backdrop-filter: blur(14px) saturate(180%);
          -webkit-backdrop-filter: blur(14px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45), 0 20px 48px rgba(0, 0, 0, 0.5);
          cursor: pointer;
          text-align: center;
          transition: all var(--duration-fast) var(--ease-out);
        }
        .quick-stat:hover { border-color: rgba(255, 255, 255, 0.3); }
        .quick-stat__value { display: block; font-size: var(--text-xl); font-weight: 600; line-height: 1.2; }
        .quick-stat__label { display: block; font-size: var(--text-xs); color: var(--text-tertiary); margin-top: 2px; text-transform: uppercase; letter-spacing: 0.06em; }
        .quick-stat--success .quick-stat__value { color: var(--status-success); }
        .quick-stat--warning .quick-stat__value { color: var(--status-warning); }
        .quick-stat--danger .quick-stat__value { color: var(--status-danger); }
        .quick-stat--active { border-color: var(--accent-primary); background: var(--accent-primary-subtle); }
        .quick-stat--success.quick-stat--active { border-color: var(--status-success); background: var(--status-success-subtle); }
        .quick-stat--warning.quick-stat--active { border-color: var(--status-warning); background: var(--status-warning-subtle); }
        .quick-stat--danger.quick-stat--active { border-color: var(--status-danger); background: var(--status-danger-subtle); }
      `}</style>
    </div>
  );
}
